import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Typography } from "@/components/Typography";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const ROUNDED_CLASSNAMES = {
  normal: "rounded-xl",
  full: "rounded-full",
} as const;

type InputRounded = keyof typeof ROUNDED_CLASSNAMES;

interface FormInputFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  placeholder?: string;
  type?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  disabled?: boolean;
  rounded?: InputRounded;
}

const FormInputField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = "text",
  leadingIcon,
  trailingIcon,
  disabled,
  rounded = "normal",
}: FormInputFieldProps<TFieldValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <div className="flex flex-col gap-1.5">
        {label && <Typography variant="button-sm">{label}</Typography>}

        <div className="relative">
          {leadingIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-body">
              {leadingIcon}
            </div>
          )}
          <Input
            {...field}
            value={field.value ?? ""}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!fieldState.error}
            className={cn(
              "h-12 border bg-white text-base shadow-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-none",
              ROUNDED_CLASSNAMES[rounded],
              leadingIcon && "pl-11",
              trailingIcon && "pr-11",
              fieldState.error &&
              "border-error-foreground focus-visible:ring-error-foreground",
            )}
          />
          {trailingIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-body">
              {trailingIcon}
            </div>
          )}
        </div>

        {fieldState.error && (
          <Typography variant="body2" className="text-error-foreground">
            {fieldState.error.message}
          </Typography>
        )}
      </div>
    )}
  />
);

export default FormInputField;
